import { type ReactNode, isValidElement } from 'react'
import { toast as sonnerToast, type ExternalToast } from 'sonner'

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ActiveToast {
  id: string | number
  count: number
}

const activeToasts = new Map<string, ActiveToast>()

function extractText(node?: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return ''
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }

  if (Array.isArray(node)) {
    return node.map(extractText).join('')
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractText(node.props.children)
  }

  return ''
}

function toastKey(type: ToastType, message: string, description?: ReactNode): string {
  return `${type}:${message}:${extractText(description)}`
}

function shakeClass(count: number): string {
  return count % 2 === 0 ? 'toast-shake-even' : 'toast-shake-odd'
}

function show(type: ToastType, message: string, options?: ExternalToast): string | number {
  const desc =
    typeof options?.description === 'function' ? options.description() : options?.description
  const key = toastKey(type, message, desc)
  const existing = activeToasts.get(key)

  const cleanup = () => activeToasts.delete(key)

  const wrapCallbacks = (opts?: ExternalToast): ExternalToast => ({
    ...opts,
    onDismiss: (t) => {
      cleanup()
      opts?.onDismiss?.(t)
    },
    onAutoClose: (t) => {
      cleanup()
      opts?.onAutoClose?.(t)
    },
  })

  if (existing) {
    existing.count++
    sonnerToast[type](message, {
      ...wrapCallbacks(options),
      id: existing.id,
      className: [options?.className, shakeClass(existing.count)].filter(Boolean).join(' '),
    })

    return existing.id
  }

  const id = sonnerToast[type](message, wrapCallbacks(options))

  activeToasts.set(key, { id, count: 1 })

  return id
}

export const toast = {
  success: (message: string, options?: ExternalToast) => show('success', message, options),
  error: (message: string, options?: ExternalToast) => show('error', message, options),
  info: (message: string, options?: ExternalToast) => show('info', message, options),
  warning: (message: string, options?: ExternalToast) => show('warning', message, options),
}
